import {
  useCreateChannel,
  useUpdateChannel,
  useGetChannels,
  useDeleteChannel,
} from "api/channels/service";
import { useUpdateSalesLocation } from "api/sales-locations/service";
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ChannelDetailForm from "./components/ChannelDetailForm";
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Form,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalBody,
} from "reactstrap";
import {
  formatPhoneNumber,
  validatePhoneNumber,
} from "../../../utils/phoneUtils";
import classNames from "classnames";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { getTheme2FormFieldsCss } from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { useGetCountries } from "api/countries/service";
import { useGetAccount } from "api/account/service";
import { useGetLocations } from "api/locations/service";
import FilesTab from "views/inventory/tabs/FilesTab";
import NotesTab from "views/inventory/tabs/NotesTab";
import { useGetChannel } from "api/channels/service";
import { getChannelTypeOptions } from "../../../constants/channelTypes";
import Lenders from "../Lenders/Lenders";
import SellerFees from "../../../components/Channels/SellerFees";
import SellerTaxes from "../../../components/Channels/SellerTaxes";
import AfterMarket from "../../../components/Channels/AfterMarket";
import Accessories from "../../../components/Channels/Accessories";
import SalesDocuments from "../../../components/Channels/SalesDocuments";
import TaxPackages from "../../../components/Channels/TaxPackages";
import TaxInfo from "../../../components/Channels/TaxInfo";
import SalesDefaults from "../../../components/Channels/SalesDefaults";
import InventoryDefaults from "../../../components/Channels/InventoryDefaults";
import BuyersGuide from "../../../components/Channels/BuyersGuide";
import LeadDefaults from "../../../components/Channels/LeadDefaults";
import ChannelAddForm from "../../../components/Channels/ChannelAddForm";
import { useGetPortfolios } from "api/portfolios/service";
import { useGetStates } from "api/states/service";
import { normalizeStatesFromApi } from "utils/normalizeStatesApi";
import { useNotification } from "../../../contexts/NotificationContext";
import SubHeader from "components/Navbars/SubHeader";

const SALES_LOCATION_BRANCH_CORE_TABS = [
  {
    id: "SALES_DEFAULT",
    translationKey: "salesDefault",
    fallbackLabel: "SALES DEFAULT",
  },
  {
    id: "INVENTORY_DEFAULTS",
    translationKey: "inventoryDefaults",
    fallbackLabel: "INVENTORY DEFAULTS",
  },
  {
    id: "BUYERS_GUIDE_DEFAULTS",
    translationKey: "buyersGuideDefaults",
    fallbackLabel: "BUYERS GUIDE DEFAULTS",
  },
  {
    id: "SELLER_FEES",
    translationKey: "sellerFees",
    fallbackLabel: "SELLER FEES",
  },
  {
    id: "SELLER_TAXES",
    translationKey: "sellerTaxes",
    fallbackLabel: "SELLER TAXES",
  },
  {
    id: "AFTER_MARKET",
    translationKey: "channelsAdd.afterMarket.AFTER_MARKET",
    fallbackLabel: "AFTER MARKET",
  },
  {
    id: "ACCESSORIES",
    translationKey: "accessories",
    fallbackLabel: "ACCESSORIES",
  },
  // {
  //   id: "TAX_PACKAGES",
  //   translationKey: "taxPackages",
  //   fallbackLabel: "TAX PACKAGES",
  // },
  { id: "TAX_INFO", translationKey: "taxInfo", fallbackLabel: "TAX INFO" },
  {
    id: "LEAD_DEFAULTS",
    translationKey: "leadDefaults",
    fallbackLabel: "LEAD DEFAULTS",
  },
];

const resolveChannelFormStateId = (state) => {
  if (state == null || state === "") return null;
  if (typeof state === "object" && state.id != null) return Number(state.id);
  const numeric = Number(state);
  return Number.isNaN(numeric) ? null : numeric;
};

const ChannelsAdd = ({
  activeColor = "secondary",
  back = false,
  onClose,
  defaultType,
  isLightTheme,
  shellThemeVariant = "1",
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const isTheme2 = shellThemeVariant === "2";
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? `${getTheme2FormFieldsCss({
            scopeSelector: ".channels-detail-page.channels-detail-page--theme2",
            fieldsSelector: ".channels-detail-form-fields",
          })}
          .channels-detail-page.channels-detail-page--theme2 .channel-detail-tabs .nav-link {
            font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
          }
          .channels-detail-page.channels-detail-page--theme2 .channels-detail-form-fields table th,
          .channels-detail-page.channels-detail-page--theme2 .channels-detail-form-fields table td {
            font-size: ${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} !important;
          }
          .channels-detail-page.channels-detail-page--theme2 .channels-detail-form-fields .card-title,
          .channels-detail-page.channels-detail-page--theme2 .channels-detail-form-fields h5,
          .channels-detail-page.channels-detail-page--theme2 .channels-detail-form-fields h6 {
            font-size: calc(${THEME2_SIDEBAR_NAV_FONT_SIZE_VAR} + 2px) !important;
          }
          .channels-detail-page.channels-detail-page--theme2 .channels-detail-form-fields .react-select__input-container input {
            min-width: 4px !important;
            opacity: 1 !important;
          }
          @media (max-width: 991.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.channels-detail-page.channels-detail-page--theme2.theme2-admin-content-compact.theme2-channels-inset {
              padding-top: calc(74px + 45px) !important;
            }
          }
          @media (max-width: 575.98px) {
            .sidebar.sidebar-shell-theme-2 ~ .main-panel > .content.channels-detail-page.channels-detail-page--theme2.theme2-admin-content-compact.theme2-channels-inset {
              padding-top: calc(64px + 45px) !important;
            }
          }`
        : "",
    [isTheme2]
  );
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [activeTab, setActiveTab] = useState("DETAIL");
  const {
    data: channel,
    isLoading: isLoadingChannel,
    isFetching,
  } = useGetChannel(id, activeTab);

  // State for merged channel data (like InventoryDetail pattern)
  const [channelMainData, setChannelMainData] = useState(null);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);

  // Reset data when ID changes to prevent showing old data
  useEffect(() => {
    setChannelMainData(null);
    setInitialDataLoaded(false);
  }, [id]);

  // Track initial data load (like InventoryDetail)
  useEffect(() => {
    if (channel && !initialDataLoaded) {
      setInitialDataLoaded(true);
    }
  }, [channel, initialDataLoaded]);

  // Merge channel data (like InventoryDetail pattern)
  useEffect(() => {
    if (channel) {
      if (!channelMainData) {
        setChannelMainData(channel);
      } else {
        setChannelMainData((prev) => ({ ...prev, ...channel }));
      }
    }
  }, [channel]);

  useEffect(() => {
    if (initialDataLoaded && isFetching && !isLoadingChannel) {
      setTabLoading(true);
      const timer = setTimeout(() => {
        setTabLoading(false);
      }, 200); // Daha kısa loading efekti
      return () => clearTimeout(timer);
    } else if (!isFetching) {
      setTabLoading(false);
    }
  }, [isFetching, isLoadingChannel, initialDataLoaded]);

  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const createChannelMutation = useCreateChannel();
  const updateChannelMutation = useUpdateChannel();
  const deleteChannelMutation = useDeleteChannel();
  const updateSalesLocationMutation = useUpdateSalesLocation();
  const { data: countriesData } = useGetCountries();
  const { data: account } = useGetAccount();
  const typeOptions = useMemo(() => getChannelTypeOptions(t), [t]);
  const { data: channelsData } = useGetChannels(
    ["bookkeeping_company"],
    1,
    100,
    ""
  );
  const bookkeepingCompanyOptions = useMemo(() => {
    if (!channelsData?.results) return [];
    return channelsData.results.map((channel) => ({
      value: channel.id,
      label: channel.name,
    }));
  }, [channelsData]);

  const { data: locationsData } = useGetLocations();
  const locationOptions = useMemo(() => {
    if (!locationsData?.results) return [];
    return locationsData.results.map((location) => ({
      value: location.location_code || location.documentId,
      label: location.location_name || location.name,
    }));
  }, [locationsData]);
  const { data: portfoliosData } = useGetPortfolios();
  const portfolioOptions = useMemo(() => {
    if (!portfoliosData?.results) return [];
    return portfoliosData.results.map((portfolio) => ({
      value: portfolio.id,
      label: portfolio.name,
    }));
  }, [portfoliosData]);

  const { data: statesData } = useGetStates();
  const registrationStateOptions = useMemo(() => {
    return normalizeStatesFromApi(statesData).map((state) => ({
      value: state.state_code || state.documentId || state.abbreviation || state.id,
      label: state.name,
    }));
  }, [statesData]);

  const [saveFunctions, setSaveFunctions] = useState({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);

  const registerSaveFunction = React.useCallback((tabId, saveFunction) => {
    setSaveFunctions((prev) => ({
      ...prev,
      [tabId]: saveFunction,
    }));
  }, []);

  useEffect(() => {
    if (!isEditMode) return undefined;

    const handleDeleteChannelRequested = () => {
      const nextAction = channelMainData?.is_active === false ? "restore" : "delete";
      setSelectedAction(nextAction);
      setDeleteModalOpen(true);
    };

    window.addEventListener("delete-channel-requested", handleDeleteChannelRequested);
    return () =>
      window.removeEventListener("delete-channel-requested", handleDeleteChannelRequested);
  }, [channelMainData?.is_active, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return undefined;

    window.dispatchEvent(
      new CustomEvent("channel-detail-action-mode", {
        detail: channelMainData?.is_active === false ? "restore" : "delete",
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent("channel-detail-action-mode", {
          detail: "delete",
        })
      );
    };
  }, [channelMainData?.is_active, isEditMode]);

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setSelectedAction(null);
  };

  const handleConfirmDelete = () => {
    if (!id || !selectedAction) return;

    if (selectedAction === "restore") {
      updateChannelMutation.mutate(
        {
          id,
          data: { is_active: true, channel_status: "active" },
        },
        {
          onSuccess: () => {
            setDeleteModalOpen(false);
            setSelectedAction(null);
          },
        }
      );
      return;
    }

    deleteChannelMutation.mutate(id, {
      onSuccess: () => {
        setDeleteModalOpen(false);
        setSelectedAction(null);
        navigate("/channels");
      },
    });
  };


  const statusOptions = [
    { value: "active", label: t("channelsAdd.active") },
    { value: "passive", label: t("channelsAdd.inactive") },
    { value: "deleted", label: t("channelsAdd.deleted") },
  ];

  const [formData, setFormData] = useState({
    channel_id: "",
    name: "",
    description: "",
    type: defaultType || "inventory_buyer",
    status: "active",
    // FED ID and Legal Name
    fed_id: "",
    legal_name: "",
    // Address Information
    address_1: "",
    address_2: "",
    zip: "",
    city: "",
    state: "",
    county: "",
    country: "",
    // Contact Information
    contact_person: "",
    phone_1: "",
    phone_2: "",
    fax_1: "",
    fax_2: "",
    website: "",
    email: "",
    // Additional Information
    text_name: "",
    accounting_class: "",
    // Sales Location Branch specific
    dealer_number: "",
    bookkeeping_company: "",
    location_code: "",
    // Sales Group (conditional)
    sales_role: false,
    fi_role: false,
    sales_manager: false,
    employee_code: "",
    // Sales Defaults
    cash_sales_book_to_this_portfolio: "",
    wholesale_sales_book_to_this_portfolio: "",
    default_contract_registration_state: "TX",
    offer_dealer_rebates: "no",
    offer_manufacturer_rebates: "no",
    default_pack_fee: "$0.00",
    trade_in_tax_cap: "$0.00",
    // Inventory Defaults
    mmr_default_region: "Southeast",
    // Default Expense Class Codes
    expense_class_available: "Expense/Make Ready",
    expense_class_evaluating: "Expense/Make Ready",
    expense_class_not_for_sale: "Expense/Make Ready",
    expense_class_recon_detail: "Expense/Make Ready",
    expense_class_check_in: "Expense/Make Ready",
    expense_class_repair_service: "Expense/Make Ready",
    expense_class_spot_delivered: "Expense/Make Ready",
    expense_class_sold: "Expense/Make Ready",
    expense_class_deleted: "Expense/Make Ready",
    expense_class_on_hold: "Expense/Make Ready",
    expense_class_pending_sale: "Expense/Make Ready",
    expense_class_in_transit: "Expense/Make Ready",
    // Floor Plan Settings
    auto_close_floor_plan_on_sale: "no",
    // Buyers Guide Defaults
    buyers_guide_option: "AS-IS",
    is_service_contract_available: "no",
    warranty_type: "",
    percentage_of_labor: "0.0000",
    percentage_of_parts: "0.0000",
    // Seller Fees
    seller_fees: [],
    // Seller Taxes
    seller_taxes: [],
    // After Market
    after_market: [],
    // Accessories
    accessories: [],
    // Sales Documents
    sales_documents: [],
    // Tax Packages
    tax_packages: [],
    // Tax Info
    tax_info: {
      // Sales Tax Information
      tax_payer_name: "",
      sales_address_1: "",
      sales_address_2: "",
      sales_zip: "",
      sales_city: "",
      sales_county: "",
      sales_country: "",
      sales_state: "",
      sales_phone_1: "",
      sales_fax_1: "",
      // VIT / Inventory Tax Information
      vit_account_number: "",
      vit_new_account_number: "",
      // Appraisal District Information
      appraisal_district_name: "",
      appraisal_address_1: "",
      appraisal_address_2: "",
      appraisal_zip: "",
      appraisal_city: "",
      appraisal_county: "",
      appraisal_country: "",
      appraisal_state: "",
      appraisal_phone_1: "",
      // Collector Information
      collector_name: "",
      collector_address_1: "",
      collector_address_2: "",
      collector_zip: "",
      collector_city: "",
      collector_county: "",
      collector_country: "",
      collector_state: "",
      collector_phone_1: "",
    },
    // Lead Defaults
    lead_defaults: {
      lead_source: "Deal Manager",
      sales_person_1: "",
      sales_person_2: "",
      fi_person: "",
      closing_probability: "50",
    },
    // Lender Defaults as nested object
    lender_defaults: {
      loans_booked_portfolio: "",
      loans_serviced_in_house: "no",
      loan_decision_method: "manual",
      default_pmt_frequency: "monthly",
      default_per_to_1st_pmt: "monthly",
      default_num_pers_to_1st_pmt: "",
      default_num_of_pmts: "",
      default_max_loan_rate: "no",
      default_loan_rate: "",
      late_fee_days: "",
      late_fee_percentage: "",
      max_num_def_downs: "",
      require_insurance: "yes",
      max_cmp_deductible: "",
      max_coll_deductible: "",
      lienholder_officer: "",
      lienholder_number: "",
      lienholder_name: "",
      lienholder_phone: "",
      lienholder_fax: "",
      lienholder_address_1: "",
      lienholder_address_2: "",
      lienholder_city: "",
      lienholder_state: "",
      lienholder_county: "",
      lienholder_zip: "",
      loss_payee_same_as_lienholder: "yes",
      side_note_document: "",
      cpi_document: "",
      arbitration: "no",
      recourse: "none",
    },
  });
  const [errors, setErrors] = useState({});
  const handleTabChange = (newTab) => setActiveTab(newTab);

  const channelType = formData.type;
  const isSalesLocationBranch = channelType === "sales_location_branch";
  const isFinanceCompany = channelType === "finance_company_bank";

  const getTabLabel = React.useCallback(
    (translationKey, fallback) => {
      const translated = t(`channelsAdd.${translationKey}`);
      return translated && translated !== `channelsAdd.${translationKey}`
        ? translated
        : fallback;
    },
    [t]
  );

  const branchTabDefinitions = React.useMemo(
    () =>
      SALES_LOCATION_BRANCH_CORE_TABS.map((tab) => ({
        ...tab,
        label: getTabLabel(tab.translationKey, tab.fallbackLabel),
      })),
    [t] // Use t directly instead of getTabLabel
  );

  const editTabItems = React.useMemo(() => {
    if (!isEditMode) {
      return [];
    }

    const tabs = [{ id: "DETAIL", label: getTabLabel("detail", "DETAIL") }];

    if (isSalesLocationBranch) {
      tabs.push(
        ...branchTabDefinitions.map(({ id, label }) => ({ id, label }))
      );
      tabs.push({
        id: "SALES_DOCUMENTS",
        label: getTabLabel("salesDocuments", "SALES DOCUMENTS"),
      });
      tabs.push({
        id: "LENDER_DEFAULTS",
        label: getTabLabel("lenderDefaults", "LENDER DEFAULTS"),
      });
    } else if (isFinanceCompany) {
      tabs.push({
        id: "SALES_DOCUMENTS",
        label: getTabLabel("salesDocuments", "SALES DOCUMENTS"),
      });
      tabs.push({
        id: "LENDER_DEFAULTS",
        label: getTabLabel("lenderDefaults", "LENDER DEFAULTS"),
      });
    }

    tabs.push(
      { id: "ECABINET", label: getTabLabel("ecabinet", "ECABINET") },
      { id: "NOTES", label: getTabLabel("notes", "NOTES") }
    );

    return tabs;
  }, [
    branchTabDefinitions,
    getTabLabel,
    isEditMode,
    isFinanceCompany,
    isSalesLocationBranch,
  ]);

  // Tab scroll helpers
  const tabScrollRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = React.useState(false);

  const checkScrollPosition = React.useCallback(() => {
    if (tabScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabScrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      setCanScrollTabsRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  React.useEffect(() => {
    if (isMobile) {
      const runCheck = () => {
        requestAnimationFrame(checkScrollPosition);
      };
      runCheck();
      const scrollElement = tabScrollRef.current;
      scrollElement?.addEventListener("scroll", checkScrollPosition);
      window.addEventListener("resize", runCheck);
      return () => {
        scrollElement?.removeEventListener("scroll", checkScrollPosition);
        window.removeEventListener("resize", runCheck);
      };
    }

    checkScrollPosition();
    const scrollElement = tabScrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener("scroll", checkScrollPosition);
      window.addEventListener("resize", checkScrollPosition);
      return () => {
        scrollElement.removeEventListener("scroll", checkScrollPosition);
        window.removeEventListener("resize", checkScrollPosition);
      };
    }
    return undefined;
  }, [checkScrollPosition, editTabItems, isMobile]);

  const handleScrollLeft = () => {
    if (tabScrollRef.current && canScrollLeft) {
      tabScrollRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (tabScrollRef.current && canScrollRight) {
      tabScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const tabIdsWithSave = React.useMemo(() => {
    const ids = new Set(["DETAIL", "LENDER_DEFAULTS"]);

    if (isSalesLocationBranch) {
      SALES_LOCATION_BRANCH_CORE_TABS.forEach((tab) => ids.add(tab.id));
      ids.add("SALES_DOCUMENTS");
    }

    // Remove save button from these tabs
    ids.delete("SELLER_FEES");
    ids.delete("SELLER_TAXES");
    ids.delete("AFTER_MARKET");
    ids.delete("ACCESSORIES");

    return ids;
  }, [isSalesLocationBranch]);

  const showSaveButton = tabIdsWithSave.has(activeTab);
  const selectedCountry = useMemo(() => {
    return countriesData?.data?.find(
      (country) => String(country.id) === String(formData.country)
    );
  }, [countriesData, formData.country]);
  const stateOptions = selectedCountry?.states || [];
  const isStateRequired =
    Array.isArray(stateOptions) && stateOptions?.length > 0;
  const countryOptions = useMemo(
    () =>
      (countriesData?.data || []).map((c) => ({
        value: String(c.id),
        label: c.name,
      })),
    [countriesData]
  );

  React.useEffect(() => {
    if (isEditMode && channel) {
      if (channel) {
        setFormData({
          channel_id: channel.channel_id,
          name: channel.name || "",
          description: channel.description || "",
          type: channel.type || "inventory",
          status: channel.channel_status || "active",
          // FED ID and Legal Name
          fed_id: channel.fed_id || "",
          legal_name: channel.legal_name || "",
          // Address Information
          address_1: channel.address_1 || "",
          address_2: channel.address_2 || "",
          zip: channel.zip || "",
          city: channel.city || "",
          state: channel.state
            ? {
              id: Number(channel.state?.id),
              abbreviation: channel.state?.abbreviation,
            }
            : "",
          county: channel.county || "",
          country: channel.country?.id ? String(channel.country.id) : "",
          // Contact Information
          contact_person: channel.contact_person || "",
          phone_1: channel.phone_1 || "",
          phone_2: channel.phone_2 || "",
          fax_1: channel.fax_1 || "",
          fax_2: channel.fax_2 || "",
          website: channel.website || "",
          email: channel.email || "",
          // Additional Information
          text_name: channel.text_name || "",
          accounting_class: channel.accounting_class || "",
          // Sales Location Branch specific
          dealer_number:
            channel.sales_location?.dealer_number ||
            channel.dealer_number ||
            "",
          bookkeeping_company:
            channel.additional_info?.bookkeeping_company?.id ||
            channel.additional_info?.bookkeeping_company ||
            channel.bookkeeping_company?.id ||
            channel.bookkeeping_company ||
            "",
          location_code:
            channel.additional_info?.location_code ||
            channel.location_code ||
            "",
          // Sales Group (conditional)
          sales_role:
            typeof channel.sales_role === "boolean"
              ? channel.sales_role
              : false,
          fi_role:
            typeof channel.fi_role === "boolean" ? channel.fi_role : false,
          sales_manager:
            typeof channel.sales_manager === "boolean"
              ? channel.sales_manager
              : false,
          employee_code: channel.employee_code || "",
          // Lender data
          lender_defaults: channel.lender_defaults
            ? {
              loans_booked_portfolio:
                channel.lender_defaults.loans_booked_portfolio || "",
              loans_serviced_in_house:
                channel.lender_defaults.loans_serviced_in_house || "no",
              loan_decision_method:
                channel.lender_defaults.loan_decision_method || "manual",
              default_pmt_frequency:
                channel.lender_defaults.default_pmt_frequency || "monthly",
              default_per_to_1st_pmt:
                channel.lender_defaults.default_per_to_1st_pmt || "monthly",
              default_num_pers_to_1st_pmt:
                channel.lender_defaults.default_num_pers_to_1st_pmt || "",
              default_num_of_pmts: channel.lender_defaults.default_num_of_pmts || "",
              default_max_loan_rate:
                channel.lender_defaults.default_max_loan_rate || "no",
              default_loan_rate: channel.lender_defaults.default_loan_rate || "",
              late_fee_days: channel.lender_defaults.late_fee_days || "",
              late_fee_percentage: channel.lender_defaults.late_fee_percentage || "",
              max_num_def_downs: channel.lender_defaults.max_num_def_downs || "",
              require_insurance: channel.lender_defaults.require_insurance || "yes",
              max_cmp_deductible: channel.lender_defaults.max_cmp_deductible || "",
              max_coll_deductible: channel.lender_defaults.max_coll_deductible || "",
              lienholder_officer: channel.lender_defaults.lienholder_officer || "",
              lienholder_number: channel.lender_defaults.lienholder_number || "",
              lienholder_name: channel.lender_defaults.lienholder_name || "",
              lienholder_phone: channel.lender_defaults.lienholder_phone || "",
              lienholder_fax: channel.lender_defaults.lienholder_fax || "",
              lienholder_address_1: channel.lender_defaults.lienholder_address_1 || "",
              lienholder_address_2: channel.lender_defaults.lienholder_address_2 || "",
              lienholder_city: channel.lender_defaults.lienholder_city || "",
              lienholder_state: channel.lender_defaults.lienholder_state || "",
              lienholder_county: channel.lender_defaults.lienholder_county || "",
              lienholder_zip: channel.lender_defaults.lienholder_zip || "",
              loss_payee_same_as_lienholder:
                channel.lender_defaults.loss_payee_same_as_lienholder || "yes",
              side_note_document: channel.lender_defaults.side_note_document || "",
              cpi_document: channel.lender_defaults.cpi_document || "",
              arbitration: channel.lender_defaults.arbitration || "no",
              recourse: channel.lender_defaults.recourse || "none",
            }
            : {
              loans_booked_portfolio: "",
              loans_serviced_in_house: "no",
              loan_decision_method: "manual",
              default_pmt_frequency: "monthly",
              default_per_to_1st_pmt: "monthly",
              default_num_pers_to_1st_pmt: "",
              default_num_of_pmts: "",
              default_max_loan_rate: "no",
              default_loan_rate: "",
              late_fee_days: "",
              late_fee_percentage: "",
              max_num_def_downs: "",
              require_insurance: "yes",
              max_cmp_deductible: "",
              max_coll_deductible: "",
              lienholder_officer: "",
              lienholder_number: "",
              lienholder_name: "",
              lienholder_phone: "",
              lienholder_fax: "",
              lienholder_address_1: "",
              lienholder_address_2: "",
              lienholder_city: "",
              lienholder_state: "",
              lienholder_county: "",
              lienholder_zip: "",
              loss_payee_same_as_lienholder: "yes",
              side_note_document: "",
              cpi_document: "",
              arbitration: "no",
              recourse: "none",
            },
          // Tax Info data
          tax_info: channel.tax_info
            ? {
              tax_payer_name: channel.tax_info.tax_payer_name || "",
              sales_address_1: channel.tax_info.sales_address_1 || "",
              sales_address_2: channel.tax_info.sales_address_2 || "",
              sales_zip: channel.tax_info.sales_zip || "",
              sales_city: channel.tax_info.sales_city || "",
              sales_county: channel.tax_info.sales_county || "",
              sales_country: channel.tax_info.sales_country || "",
              sales_state: channel.tax_info.sales_state || "",
              sales_phone_1: channel.tax_info.sales_phone_1 || "",
              sales_fax_1: channel.tax_info.sales_fax_1 || "",
              vit_account_number: channel.tax_info.vit_account_number || "",
              vit_new_account_number:
                channel.tax_info.vit_new_account_number || "",
              appraisal_district_name:
                channel.tax_info.appraisal_district_name || "",
              appraisal_address_1: channel.tax_info.appraisal_address_1 || "",
              appraisal_address_2: channel.tax_info.appraisal_address_2 || "",
              appraisal_zip: channel.tax_info.appraisal_zip || "",
              appraisal_city: channel.tax_info.appraisal_city || "",
              appraisal_county: channel.tax_info.appraisal_county || "",
              appraisal_country: channel.tax_info.appraisal_country || "",
              appraisal_state: channel.tax_info.appraisal_state || "",
              appraisal_phone_1: channel.tax_info.appraisal_phone_1 || "",
              collector_name: channel.tax_info.collector_name || "",
              collector_address_1: channel.tax_info.collector_address_1 || "",
              collector_address_2: channel.tax_info.collector_address_2 || "",
              collector_zip: channel.tax_info.collector_zip || "",
              collector_city: channel.tax_info.collector_city || "",
              collector_county: channel.tax_info.collector_county || "",
              collector_country: channel.tax_info.collector_country || "",
              collector_state: channel.tax_info.collector_state || "",
              collector_phone_1: channel.tax_info.collector_phone_1 || "",
            }
            : {
              tax_payer_name: "",
              sales_address_1: "",
              sales_address_2: "",
              sales_zip: "",
              sales_city: "",
              sales_county: "",
              sales_country: "",
              sales_state: "",
              sales_phone_1: "",
              sales_fax_1: "",
              vit_account_number: "",
              vit_new_account_number: "",
              appraisal_district_name: "",
              appraisal_address_1: "",
              appraisal_address_2: "",
              appraisal_zip: "",
              appraisal_city: "",
              appraisal_county: "",
              appraisal_country: "",
              appraisal_state: "",
              appraisal_phone_1: "",
              collector_name: "",
              collector_address_1: "",
              collector_address_2: "",
              collector_zip: "",
              collector_city: "",
              collector_county: "",
              collector_country: "",
              collector_state: "",
              collector_phone_1: "",
            },
          // Lead Defaults data
          lead_defaults: channel.lead_defaults
            ? {
              lead_source:
                channel.lead_defaults.lead_source || "Deal Manager",
              sales_person_1: channel.lead_defaults.sales_person_1 || "",
              sales_person_2: channel.lead_defaults.sales_person_2 || "",
              fi_person: channel.lead_defaults.fi_person || "",
              closing_probability:
                channel.lead_defaults.closing_probability || "50",
            }
            : {
              lead_source: "Deal Manager",
              sales_person_1: "",
              sales_person_2: "",
              fi_person: "",
              closing_probability: "50",
            },
        });
      }
    }
  }, [isEditMode, channel, id]);
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Check if this is a nested lender field
    if (name.startsWith("lender_defaults.")) {
      const fieldName = name.replace("lender_defaults.", "");

      // Handle phone number formatting for lender fields
      let formattedValue = value;
      if (fieldName.includes("phone") || fieldName.includes("fax")) {
        formattedValue = formatPhoneNumber(value);
      }

      setFormData((prev) => ({
        ...prev,
        lender_defaults: {
          ...prev.lender_defaults,
          [fieldName]: type === "checkbox" ? checked : formattedValue,
        },
      }));

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    } else {
      // Handle phone number formatting for regular fields
      let formattedValue = value;
      if (name.includes("phone") || name.includes("fax")) {
        formattedValue = formatPhoneNumber(value);
      }

      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : formattedValue,
        ...(name === "country" ? { state: "" } : {}),
      }));

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    }
  };

  // react-select change handlers
  const handleSingleSelectChange = (name) => (option) => {
    const nextValue = option ? option.value : "";
    // When country changes, clear state
    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
      ...(name === "country" ? { state: "" } : {}),
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  function mapValueToOption(value, options) {
    if (!options) return null;
    return (
      options.find((opt) => String(opt.value ?? opt.id) === String(value)) ||
      null
    );
  }

  const validateForm = () => {
    const newErrors = {};
    const missingFields = [];

    // Required fields
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = t("channelsAdd.nameRequired");
      missingFields.push(t("channelsAdd.descriptiveName"));
    }
    if (!formData.legal_name || !formData.legal_name.trim()) {
      newErrors.legal_name = t("channelsAdd.legalNameRequired");
      missingFields.push(t("channelsAdd.legalName"));
    }
    if (!formData.address_1 || !formData.address_1.trim()) {
      newErrors.address_1 = t("channelsAdd.address1Required");
      missingFields.push(t("channelsAdd.address1"));
    }
    if (!formData.zip || !formData.zip.trim()) {
      newErrors.zip = t("channelsAdd.zipRequired");
      missingFields.push(t("channelsAdd.zip"));
    }
    if (!formData.city) {
      newErrors.city = t("channelsAdd.cityRequired");
      missingFields.push(t("channelsAdd.city"));
    }
    const hasStates =
      Array.isArray(selectedCountry?.states) &&
      selectedCountry.states?.length > 0;
    if (hasStates && resolveChannelFormStateId(formData.state) == null) {
      newErrors.state = t("channelsAdd.stateRequired");
      missingFields.push(t("channelsAdd.state"));
    }
    // if (!formData.country) {
    //   newErrors.country = t("channelsAdd.countryRequired");
    //   missingFields.push(t("channelsAdd.country"));
    // }
    if (!formData.phone_1 || !formData.phone_1.trim()) {
      newErrors.phone_1 = t("channelsAdd.phone1Required");
      missingFields.push(t("channelsAdd.phone1"));
    } else {
      const phoneError = validatePhoneNumber(formData.phone_1);
      if (phoneError) {
        newErrors.phone_1 = phoneError;
        missingFields.push(t("channelsAdd.phone1"));
      }
    }

    setErrors(newErrors);

    // Show notification if there are errors
    if (Object.keys(newErrors).length > 0) {
      const fieldsList = missingFields.join(', ');
      showNotification(
        'danger',
        `${t('channelsAdd.requiredFieldsMissing')}: ${fieldsList}`
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // Check if we're in a sales location branch core tab
    const isSalesLocationBranchCoreTab =
      isSalesLocationBranch &&
      SALES_LOCATION_BRANCH_CORE_TABS.some((tab) => tab.id === activeTab);

    // Call specific save function for SALES_DEFAULT tab
    if (activeTab === "SALES_DEFAULT" && saveFunctions["SALES_DEFAULT"]) {
      saveFunctions["SALES_DEFAULT"]();
      return;
    }

    if (
      activeTab === "INVENTORY_DEFAULTS" &&
      saveFunctions["INVENTORY_DEFAULTS"]
    ) {
      saveFunctions["INVENTORY_DEFAULTS"]();
      return;
    }

    if (
      activeTab === "BUYERS_GUIDE_DEFAULTS" &&
      saveFunctions["BUYERS_GUIDE_DEFAULTS"]
    ) {
      saveFunctions["BUYERS_GUIDE_DEFAULTS"]();
      return;
    }

    if (activeTab === "TAX_INFO" && saveFunctions["TAX_INFO"]) {
      saveFunctions["TAX_INFO"]();
      return;
    }

    if (activeTab === "LEAD_DEFAULTS" && saveFunctions["LEAD_DEFAULTS"]) {
      saveFunctions["LEAD_DEFAULTS"]();
      return;
    }

    if (isEditMode) {
      if (isSalesLocationBranchCoreTab) {
        // Use sales-locations endpoint for sales location branch core tabs
        updateSalesLocationMutation.mutate({
          id: formData.channel_id,
          data: {
            name: formData.name,
            type: formData.type,
            channel_status: formData.status,
            description: formData.description,
            // FED ID and Legal Name
            fed_id: formData.fed_id,
            legal_name: formData.legal_name,
            // Address Information
            address_1: formData.address_1,
            address_2: formData.address_2,
            zip: formData.zip,
            city: formData.city,
            state: resolveChannelFormStateId(formData.state),
            county: formData.county,
            country: formData.country === "" ? null : Number(formData.country),
            ...(formData.type === "sales_group_person"
              ? {
                sales_role: !!formData.sales_role,
                fi_role: !!formData.fi_role,
                sales_manager: !!formData.sales_manager,
                employee_code: formData.employee_code || "",
              }
              : {}),
            // Contact Information
            contact_person: formData.contact_person,
            phone_1: formData.phone_1,
            phone_2: formData.phone_2,
            fax_1: formData.fax_1,
            fax_2: formData.fax_2,
            website: formData.website,
            email: formData.email,
            // Additional Information
            text_name: formData.text_name,
            accounting_class: formData.accounting_class,
            // Lender Information omitted here per user request
            // Sales Location specific data
            ...(channelMainData.type === "sales_location_branch" ? {
              additional_info: {
                dealer_number: formData.dealer_number,
                bookkeeping_company: formData.bookkeeping_company,
                location_code: formData.location_code
              }
            } : {}),
            // Sales location branch specific data
            cash_sales_book_to_this_portfolio:
              formData.cash_sales_book_to_this_portfolio,
            wholesale_sales_book_to_this_portfolio:
              formData.wholesale_sales_book_to_this_portfolio,
            default_contract_registration_state:
              formData.default_contract_registration_state,
            offer_dealer_rebates: formData.offer_dealer_rebates,
            offer_manufacturer_rebates: formData.offer_manufacturer_rebates,
            default_pack_fee: formData.default_pack_fee,
            trade_in_tax_cap: formData.trade_in_tax_cap,
            // Inventory defaults
            mmr_default_region: formData.mmr_default_region,
            expense_class_available: formData.expense_class_available,
            expense_class_evaluating: formData.expense_class_evaluating,
            expense_class_not_for_sale: formData.expense_class_not_for_sale,
            expense_class_recon_detail: formData.expense_class_recon_detail,
            expense_class_check_in: formData.expense_class_check_in,
            expense_class_repair_service: formData.expense_class_repair_service,
            expense_class_spot_delivered: formData.expense_class_spot_delivered,
            expense_class_sold: formData.expense_class_sold,
            expense_class_deleted: formData.expense_class_deleted,
            expense_class_on_hold: formData.expense_class_on_hold,
            expense_class_pending_sale: formData.expense_class_pending_sale,
            expense_class_in_transit: formData.expense_class_in_transit,
            auto_close_floor_plan_on_sale:
              formData.auto_close_floor_plan_on_sale,
            // Buyers guide
            buyers_guide_option: formData.buyers_guide_option,
            is_service_contract_available:
              formData.is_service_contract_available,
            warranty_type: formData.warranty_type,
            percentage_of_labor: formData.percentage_of_labor,
            percentage_of_parts: formData.percentage_of_parts,
            // Seller fees and taxes
            seller_fees: formData.seller_fees,
            seller_taxes: formData.seller_taxes,
            // After market and accessories
            after_market: formData.after_market,
            accessories: formData.accessories,
            // Tax packages and info
            tax_packages: formData.tax_packages,
            tax_info: formData.tax_info,
          },
        });
      } else if (activeTab === "LENDER_DEFAULTS") {
        updateChannelMutation.mutate({
          id: formData.channel_id,
          data: {
            lender_defaults: formData.lender_defaults,
          },
        });
      } else {
        // Use regular channels endpoint for other tabs
        updateChannelMutation.mutate({
          id: formData.channel_id,
          data: {
            name: formData.name,
            type: formData.type,
            channel_status: formData.status,
            description: formData.description,
            // FED ID and Legal Name
            fed_id: formData.fed_id,
            legal_name: formData.legal_name,
            // Address Information
            address_1: formData.address_1,
            address_2: formData.address_2,
            zip: formData.zip,
            city: formData.city,
            state: resolveChannelFormStateId(formData.state),
            county: formData.county,
            country: formData.country === "" ? null : Number(formData.country),
            ...(formData.type === "sales_group_person"
              ? {
                sales_role: !!formData.sales_role,
                fi_role: !!formData.fi_role,
                sales_manager: !!formData.sales_manager,
                employee_code: formData.employee_code || "",
              }
              : {}),
            // Contact Information
            ...(channelMainData.type === "sales_location_branch" ? {
              additional_info: {
                dealer_number: formData.dealer_number,
                bookkeeping_company: formData.bookkeeping_company,
                location_code: formData.location_code
              }
            } : {}),
            contact_person: formData.contact_person,
            phone_1: formData.phone_1,
            phone_2: formData.phone_2,
            fax_1: formData.fax_1,
            fax_2: formData.fax_2,
            website: formData.website,
            email: formData.email,
            // Additional Information
            text_name: formData.text_name,
            accounting_class: formData.accounting_class,
            // Lender Information omitted here per user request
          },
        });
      }
    } else {
      createChannelMutation.mutate({
        name: formData.name,
        type: formData.type,
        description: formData.description,
        channel_status: formData.status,
        // FED ID and Legal Name
        fed_id: formData.fed_id,
        legal_name: formData.legal_name,
        // Address Information
        address_1: formData.address_1,
        address_2: formData.address_2,
        zip: formData.zip,
        city: formData.city == "" ? null : formData.city,
        state: resolveChannelFormStateId(formData.state),
        county: formData.county,
        country: formData.country == "" ? null : Number(formData.country),
        ...(formData.type === "sales_group_person"
          ? {
            sales_role: !!formData.sales_role,
            fi_role: !!formData.fi_role,
            sales_manager: !!formData.sales_manager,
            employee_code: formData.employee_code || "",
          }
          : {}),
        // Contact Information
        contact_person: formData.contact_person,
        phone_1: formData.phone_1,
        phone_2: formData.phone_2,
        fax_1: formData.fax_1,
        fax_2: formData.fax_2,
        website: formData.website,
        email: formData.email,
        // Additional Information
        text_name: formData.text_name,
        accounting_class: formData.accounting_class,
        // Lender Information
        lender_defaults: formData.lender_defaults,
        // Sales Location specific data
        ...(formData.type === "sales_location_branch" ? {
          additional_info: {
            dealer_number: formData.dealer_number,
            bookkeeping_company: formData.bookkeeping_company,
            location_code: formData.location_code
          }
        } : {}),
      });
    }
  };

  const handleCancel = () => {
    if (back && typeof onClose === "function") {
      onClose();
    } else {
      navigate("/channels");
    }
  };

  const formatChannelTypeTitle = (typeValue) => {
    if (!typeValue) return "";
    return String(typeValue)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const subHeaderTitle = useMemo(() => {
    if (!isEditMode) {
      return t("channelsAdd.title");
    }

    const channelId = channelMainData?.channel_id || id;
    const type = channelMainData?.type || formData.type || "";
    const typeLabel = type
      ? t(`channels.types.${type}`, {
          defaultValue: formatChannelTypeTitle(type),
        })
      : "";

    return `Channel ${channelId} / ${typeLabel}`;
  }, [
    isEditMode,
    channelMainData?.channel_id,
    channelMainData?.type,
    formData.type,
    id,
    t,
  ]);

  return (
    <div
      className={classNames("content", "channels-detail-page", {
        "channels-detail-page--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-channels-inset": isTheme2,
      })}
      style={{
        minHeight: "80vh",
        paddingTop: isMobile && !isTheme2 ? "7rem" : undefined,
      }}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <div style={{ width: "100%" }}>
      <div className="channels-detail-subheader-wrap">
        <SubHeader
          activeColor={activeColor}
          back={-1}
          backFallback="/channels"
          title={subHeaderTitle}
          status={channelMainData?.channel_status}
          theme2ScrollGapFill={shellThemeVariant === "2"}
          isLightTheme={isLightTheme}
        />
      </div>
      <Row>
        <Col md="12">
          {isEditMode && (
            <div
              className={classNames("channel-detail-tabs w-100", {
                "detail-mobile-tabs": isMobile,
              })}
            >
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between">
                {isMobile ? (
                  <div className="d-flex align-items-center flex-grow-1 min-width-0 w-100">
                    <div
                      ref={tabScrollRef}
                      className="flex-grow-1 channel-tabs-scroll"
                      style={{ minWidth: 0 }}
                    >
                      <Nav
                        tabs
                        className="border-0"
                        style={{
                          borderBottom: "none",
                          display: "flex",
                          flexDirection: "row",
                          flexWrap: "nowrap",
                          minWidth: "max-content",
                          width: "auto",
                          paddingLeft: 0,
                          paddingRight: 0,
                        }}
                      >
                        {editTabItems.map((tab) => (
                          <NavItem key={tab.id} style={{ flex: "0 0 auto" }}>
                            <NavLink
                              href="#"
                              className={
                                activeTab === tab.id
                                  ? "active font-weight-bold"
                                  : "text-muted"
                              }
                              style={{
                                background: "none",
                                border: "none",
                                fontSize: 14,
                                position: "relative",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                color:
                                  activeTab === tab.id
                                    ? activeColorStyle.borderColor
                                    : undefined,
                                fontWeight: activeTab === tab.id ? 700 : 500,
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                handleTabChange(tab.id);
                              }}
                            >
                              {tab.label}
                              {activeTab === tab.id && (
                                <div
                                  style={{
                                    height: 3,
                                    background: activeColorStyle.borderColor,
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    bottom: -8,
                                  }}
                                />
                              )}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </div>
                    {canScrollTabsRight && (
                      <button
                        type="button"
                        className="btn btn-link p-0 flex-shrink-0"
                        onClick={handleScrollRight}
                        style={{
                          color: activeColorStyle.borderColor,
                          lineHeight: 1,
                        }}
                        aria-label={t("common.scrollTabs")}
                      >
                        <i
                          className="tim-icons icon-minimal-right"
                          style={{ fontSize: 20 }}
                        />
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div
                      className="channel-tabs-scroll w-100 flex-grow-1"
                      ref={tabScrollRef}
                    >
                      <Nav
                        tabs
                        className="border-0"
                        style={{
                          borderBottom: "none",
                          flexWrap: "nowrap",
                          minWidth: "max-content",
                        }}
                      >
                        {editTabItems.map((tab) => (
                          <NavItem key={tab.id} style={{ flex: "0 0 auto" }}>
                            <NavLink
                              href="#"
                              className={
                                activeTab === tab.id
                                  ? "active font-weight-bold"
                                  : "text-muted"
                              }
                              style={{
                                background: "none",
                                border: "none",
                                fontSize: 14,
                                position: "relative",
                                cursor: "pointer",
                                color:
                                  activeTab === tab.id
                                    ? activeColorStyle.borderColor
                                    : undefined,
                                fontWeight: activeTab === tab.id ? 700 : 500,
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                handleTabChange(tab.id);
                              }}
                            >
                              {tab.label}
                              {activeTab === tab.id && (
                                <div
                                  style={{
                                    height: 3,
                                    background: activeColorStyle.borderColor,
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    bottom: -8,
                                  }}
                                />
                              )}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </div>

                    <div className="d-none d-lg-flex align-items-center flex-shrink-0">
                      <Button
                        size="sm"
                        color="link"
                        onClick={handleScrollLeft}
                        disabled={!canScrollLeft}
                        style={{
                          padding: "0px 5px",
                          cursor: canScrollLeft ? "pointer" : "not-allowed",
                          color: activeColorStyle.borderColor,
                          opacity: canScrollLeft ? 1 : 0.3,
                        }}
                      >
                        <i
                          className="tim-icons icon-minimal-left"
                          style={{ fontSize: "20px" }}
                        />
                      </Button>
                      <Button
                        size="sm"
                        color="link"
                        onClick={handleScrollRight}
                        disabled={!canScrollRight}
                        style={{
                          padding: "0px 5px",
                          cursor: canScrollRight ? "pointer" : "not-allowed",
                          color: activeColorStyle.borderColor,
                          opacity: canScrollRight ? 1 : 0.3,
                        }}
                      >
                        <i
                          className="tim-icons icon-minimal-right"
                          style={{ fontSize: "20px" }}
                        />
                      </Button>
                    </div>
                  </>
                )}

                <div className="d-none d-lg-flex align-items-center flex-shrink-0 ml-lg-2">
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    onClick={handleCancel}
                    disabled={
                      createChannelMutation.isPending || updateChannelMutation.isPending
                    }
                    className="mr-2"
                  >
                    {t("channelsAdd.cancel")}
                  </Button>

                  {showSaveButton && (
                    <button
                      className="btn btn-sm"
                      style={{ ...activeColorStyle, color: "white", margin: 0 }}
                      onClick={handleSubmit}
                      disabled={updateChannelMutation.isPending}
                      type="button"
                    >
                      {updateChannelMutation.isPending ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          {t("common.saving")}
                        </>
                      ) : (
                        t("common.save")
                      )}
                    </button>
                  )}
                </div>

                <div className="d-flex d-lg-none flex-column w-100 mt-2">
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    onClick={handleCancel}
                    disabled={
                      createChannelMutation.isPending || updateChannelMutation.isPending
                    }
                    className="w-100 mb-2"
                  >
                    {t("channelsAdd.cancel")}
                  </Button>

                  {showSaveButton && (
                    <button
                      className="btn btn-sm w-100"
                      style={{
                        ...activeColorStyle,
                        color: "white",
                        padding: "12px",
                        margin: 0,
                      }}
                      onClick={handleSubmit}
                      disabled={updateChannelMutation.isPending}
                      type="button"
                    >
                      {updateChannelMutation.isPending ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm mr-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          {t("common.saving")}
                        </>
                      ) : (
                        t("common.save")
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {isEditMode ? (
            ((isLoadingChannel && !channelMainData) || tabLoading) ? (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ minHeight: "400px" }}
              >
                <Spinner color="primary" className="mr-2" />
                <span className="text-muted">
                  {t("channelsAdd.loadingChannelInfo")}
                </span>
              </div>
            ) : (
              <>
                <TabContent
                  activeTab={activeTab}
                  className={classNames({ "channels-detail-form-fields": isTheme2 })}
                >
                  <TabPane tabId="DETAIL">
                    <Card>
                      {" "}
                      <CardBody>
                        <ChannelDetailForm
                          formData={formData}
                          setFormData={setFormData}
                          handleInputChange={handleInputChange}
                          handleSingleSelectChange={handleSingleSelectChange}
                          mapValueToOption={mapValueToOption}
                          errors={errors}
                          activeColor={activeColor}
                          isTheme2={isTheme2}
                          statusOptions={statusOptions}
                          countryOptions={countryOptions}
                          bookkeepingCompanyOptions={bookkeepingCompanyOptions}
                          locationOptions={locationOptions}
                          typeOptions={typeOptions}
                          isEditMode={isEditMode}
                          isStateRequired={isStateRequired}
                          channelType={channelType}
                          createChannelMutation={createChannelMutation}
                          updateChannelMutation={updateChannelMutation}
                          account={account}
                          t={t}
                        />
                      </CardBody>
                    </Card>
                  </TabPane>
                  {isSalesLocationBranch &&
                    branchTabDefinitions.map((tab) => (
                      <TabPane tabId={tab.id} key={tab.id}>
                        {activeTab === tab.id && (
                          <>
                            {tab.id === "SALES_DEFAULT" ? (
                              <Card>
                                <CardBody>
                                  <SalesDefaults
                                    formData={channelMainData?.sales_location}
                                    activeColor={activeColor}
                                    isTheme2={isTheme2}
                                    portfolioOptions={portfolioOptions || []}
                                    salesLocationId={
                                      channelMainData?.sales_location?.documentId
                                    }
                                    onSave={registerSaveFunction}
                                  />
                                </CardBody>
                              </Card>
                            ) : tab.id === "INVENTORY_DEFAULTS" ? (
                              <Card>
                                <CardBody>
                                  <InventoryDefaults
                                    formData={
                                      channelMainData?.sales_location
                                        ?.inventory_defaults
                                    }
                                    activeColor={activeColor}
                                    isTheme2={isTheme2}
                                    inventoryStatusOptions={[
                                      {
                                        value: "expense_make_ready",
                                        label: "Expense Make Ready",
                                      },
                                      {
                                        value: "expense_detail",
                                        label: "Expense Detail",
                                      },
                                      {
                                        value: "expense_service",
                                        label: "Expense Service",
                                      },
                                    ]}
                                    salesLocationId={
                                      channelMainData?.sales_location?.documentId
                                    }
                                    onSave={registerSaveFunction}
                                  />
                                </CardBody>
                              </Card>
                            ) : tab.id === "BUYERS_GUIDE_DEFAULTS" ? (
                              <Card>
                                <CardBody>
                                  <BuyersGuide
                                    formData={
                                      channelMainData?.sales_location
                                        ?.buyers_guide_defaults
                                    }
                                    setFormData={setFormData}
                                    activeColor={activeColor}
                                    isTheme2={isTheme2}
                                    salesLocationId={
                                      channelMainData?.sales_location?.documentId
                                    }
                                    onSave={registerSaveFunction}
                                  />
                                </CardBody>
                              </Card>
                            ) : tab.id === "SELLER_FEES" ? (
                              <Card>
                                <CardBody>
                                  <SellerFees
                                    formData={channelMainData?.sales_location}
                                    state={channelMainData?.state}
                                    setFormData={setFormData}
                                    activeColor={activeColor}
                                    isTheme2={isTheme2}
                                    activeColorStyle={activeColorStyle}
                                    salesLocationId={
                                      channelMainData?.sales_location?.documentId
                                    }
                                  />
                                </CardBody>
                              </Card>
                            ) : tab.id === "SELLER_TAXES" ? (
                              <Card>
                                <CardBody>
                                  <SellerTaxes
                                    formData={channelMainData?.sales_location}
                                    setFormData={setFormData}
                                    activeColorStyle={activeColorStyle}
                                    activeColor={activeColor}
                                    isTheme2={isTheme2}
                                    stateOptions={registrationStateOptions}
                                    salesLocationId={
                                      channelMainData?.sales_location?.documentId
                                    }
                                  />
                                </CardBody>
                              </Card>
                            ) : tab.id === "AFTER_MARKET" ? (
                              <Card>
                                <CardBody>
                                  <AfterMarket
                                    data={
                                      channelMainData?.sales_location
                                        ?.after_market
                                    }
                                    setFormData={setFormData}
                                    activeColorStyle={activeColorStyle}
                                    activeColor={activeColor}
                                    isTheme2={isTheme2}
                                    salesLocationId={
                                      channelMainData?.sales_location?.documentId
                                    }
                                  />
                                </CardBody>
                              </Card>
                            ) : tab.id === "ACCESSORIES" ? (
                              <Card>
                                <CardBody>
                                  <Accessories
                                    formData={channelMainData?.sales_location}
                                    setFormData={(updater) => {
                                      setChannelMainData((prev) => ({
                                        ...prev,
                                        sales_location:
                                          typeof updater === "function"
                                            ? updater(prev?.sales_location || {})
                                            : {
                                                ...(prev?.sales_location || {}),
                                                ...updater,
                                              },
                                      }));
                                    }}
                                    activeColorStyle={activeColorStyle}
                                    isTheme2={isTheme2}
                                    salesLocationId={
                                      channelMainData?.sales_location?.documentId
                                    }
                                  />
                                </CardBody>
                              </Card>
                            ) : tab.id === "TAX_PACKAGES" ? (
                              <Card>
                                <CardBody>
                                  <TaxPackages
                                    formData={
                                      channelMainData?.sales_location
                                        ?.tax_packages
                                    }
                                    setFormData={setFormData}
                                    activeColorStyle={activeColorStyle}
                                    salesLocationId={
                                      channelMainData?.sales_location?.documentId
                                    }
                                  />
                                </CardBody>
                              </Card>
                            ) : tab.id === "TAX_INFO" ? (
                              <TaxInfo
                                formData={{
                                  ...channelMainData?.sales_location?.tax_info,
                                  ...channelMainData?.sales_location?.sales_tax_info
                                }}
                                setFormData={setFormData}
                                activeColor={activeColor}
                                isTheme2={isTheme2}
                                salesLocationId={
                                  channelMainData?.sales_location?.documentId
                                }
                                onSave={registerSaveFunction}
                                disableCard={true}
                                salesTaxes={channelMainData?.sales_location?.sales_taxes || []}
                              />
                            ) : tab.id === "LEAD_DEFAULTS" ? (
                              <Card>
                                <CardBody>
                                  <LeadDefaults
                                    formData={
                                      channelMainData?.sales_location
                                        ?.lead_defaults
                                    }
                                    setFormData={setFormData}
                                    activeColor={activeColor}
                                    isTheme2={isTheme2}
                                    salesLocationId={
                                      channelMainData?.sales_location?.documentId
                                    }
                                    onSave={registerSaveFunction}
                                  />
                                </CardBody>
                              </Card>
                            ) : (
                              <div className="p-4 text-muted">
                                {t("channelsAdd.tabPlaceholder", {
                                  section: tab.label,
                                })}
                              </div>
                            )}
                          </>
                        )}
                      </TabPane>
                    ))}
                  {(isSalesLocationBranch || isFinanceCompany) && (
                    <TabPane tabId="SALES_DOCUMENTS">
                      <Card>
                        <CardBody>
                          <SalesDocuments
                            formData={channelMainData?.sales_documents}
                            setFormData={setFormData}
                            isLightTheme={isLightTheme}
                            isTheme2={isTheme2}
                            state={channelMainData?.state?.id}
                            activeColorStyle={activeColorStyle}
                            activeColor={activeColor}
                            salesLocationId={
                              channelMainData?.channel_id
                            }
                          />
                        </CardBody>
                      </Card>
                    </TabPane>
                  )}
                  {(isSalesLocationBranch || isFinanceCompany) && (
                    <TabPane tabId="LENDER_DEFAULTS">
                      <Card>
                        <CardBody>
                          <Lenders
                            formData={formData}
                            handleInputChange={handleInputChange}
                            activeColor={activeColor}
                            isTheme2={isTheme2}
                          />
                        </CardBody>
                      </Card>
                    </TabPane>
                  )}
                  <TabPane tabId="ECABINET">
                    <FilesTab
                      module="channels"
                      type="ecabinet"
                      permission={{
                        add_files: account?.permissions?.channels_ecabinet_add,
                        delete_files: account?.permissions?.channels_ecabinet_add,
                      }}
                      id={channelMainData?.id}
                      data={channelMainData?.ecabinets}
                      isLoading={false}
                      activeColor={activeColor}
                      isTheme2={isTheme2}
                    />
                  </TabPane>
                  <TabPane tabId="NOTES">
                    <NotesTab
                      module="channels"
                      id={channelMainData?.id}
                      permission={{
                        notes_add: account?.permissions?.channels_notes_add,
                      }}
                      activeColor={activeColor}
                      isTheme2={isTheme2}
                    />
                  </TabPane>
                </TabContent>
              </>
            )
          ) : (
            <Card>
              <CardBody
                className={classNames({ "channels-detail-form-fields": isTheme2 })}
              >
                <Form onSubmit={handleSubmit}>
                  <ChannelAddForm
                    formData={formData}
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                    handleInputChange={handleInputChange}
                    handleSingleSelectChange={handleSingleSelectChange}
                    mapValueToOption={mapValueToOption}
                    errors={errors}
                    statusOptions={statusOptions}
                    countryOptions={countryOptions}
                    typeOptions={typeOptions}
                    isStateRequired={isStateRequired}
                    createChannelMutation={createChannelMutation}
                    updateChannelMutation={updateChannelMutation}
                    updateSalesLocationMutation={updateSalesLocationMutation}
                    back={back}
                    isEditMode={isEditMode}
                    handleCancel={handleCancel}
                    activeColorStyle={activeColorStyle}
                    t={t}
                  />
                </Form>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
      </div>

      <Modal
        isOpen={deleteModalOpen}
        toggle={handleCancelDelete}
        centered={false}
        style={{ marginTop: "100px" }}
      >
        <div className="modal-header justify-content-center">
          <button
            aria-hidden={true}
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={handleCancelDelete}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">
            {selectedAction === "restore"
              ? t("channels.restoreChannel")
              : t("channels.deleteChannel")}
          </h6>
        </div>
        <ModalBody className="text-center">
          <p>
            {selectedAction === "restore"
              ? t("channels.restoreConfirmation")
              : t("channels.deleteConfirmation")}
          </p>
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="secondary"
            type="button"
            onClick={handleCancelDelete}
            disabled={deleteChannelMutation.isPending || updateChannelMutation.isPending}
          >
            {t("channels.cancel")}
          </Button>
          <Button
            style={{
              background:
                selectedAction === "restore"
                  ? "linear-gradient(to bottom left, #00bf72, #00a651, #00bf72)"
                  : "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              backgroundImage:
                selectedAction === "restore"
                  ? "linear-gradient(to bottom left, #00bf72, #00a651, #00bf72)"
                  : "linear-gradient(to bottom left, #fd5d93, #ec250d, #fd5d93)",
              borderColor: selectedAction === "restore" ? "#00a651" : "#fd5d93",
              color: "white",
            }}
            color="danger"
            type="button"
            onClick={handleConfirmDelete}
            disabled={deleteChannelMutation.isPending || updateChannelMutation.isPending}
          >
            {deleteChannelMutation.isPending || updateChannelMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                />
                {selectedAction === "restore"
                  ? t("channels.restoring")
                  : t("channels.deleting")}
              </>
            ) : selectedAction === "restore" ? (
              t("channels.yesRestore")
            ) : (
              t("channels.yesDelete")
            )}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ChannelsAdd;
